import { CalendarRange, X } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { useDialogFocusTrap } from '../../hooks/useDialogFocusTrap';
import { useI18n } from '../../i18n/useI18n';
import type { Transaction } from '../../lib/api-types';
import { useUpdateTransaction } from '../transactions/transactions.api';
import { formatLongDate } from './dashboard-format';
import { transactionToWrite } from './transaction-write';

export function LimitMovementDialog({
  onClose,
  transaction,
}: {
  readonly onClose: () => void;
  readonly transaction: Transaction;
}) {
  const { locale, t } = useI18n();
  const [limitDate, setLimitDate] = useState(transaction.limitDate ?? '');
  const updateTransaction = useUpdateTransaction();
  const dialogRef = useDialogFocusTrap(onClose);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await updateTransaction.mutateAsync({
      id: transaction.id,
      input: transactionToWrite(transaction, { limitDate: limitDate || null }),
    });
    onClose();
  };

  return (
    <div className="dialog-backdrop" role="presentation">
      <section
        aria-labelledby="limit-movement-title"
        aria-modal="true"
        className="movement-dialog limit-movement-sheet"
        ref={dialogRef}
        role="dialog"
      >
        <div className="dialog-heading">
          <div>
            <p className="section-kicker">{t('dashboard.frequency')}</p>
            <h2 id="limit-movement-title">{t('plan.limitTitle')}</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            aria-label={t('common.close')}
            onClick={onClose}
          >
            <X aria-hidden="true" />
          </button>
        </div>

        <p className="limit-description">{t('plan.limitDescription')}</p>
        <form className="movement-form" onSubmit={submit}>
          <div className="limit-range-summary field-wide">
            <CalendarRange aria-hidden="true" />
            <div>
              <span>{t('plan.limitStart')}</span>
              <strong>{formatLongDate(transaction.date, locale)}</strong>
            </div>
            <div>
              <span>{t('plan.limitEnd')}</span>
              <strong>
                {limitDate ? formatLongDate(limitDate, locale) : t('plan.limitClear')}
              </strong>
            </div>
          </div>
          <label className="field-wide">
            {t('dashboard.dateLimit')}
            <input
              min={transaction.date}
              type="date"
              value={limitDate}
              onChange={(event) => setLimitDate(event.target.value)}
            />
          </label>
          {updateTransaction.error !== null && (
            <p className="form-error field-wide" role="alert">
              {t('dashboard.saveError')}
            </p>
          )}
          <div className="dialog-actions field-wide">
            <button className="button button-ghost" type="button" onClick={() => setLimitDate('')}>
              {t('plan.limitClear')}
            </button>
            <button
              className="button button-primary"
              disabled={updateTransaction.isPending}
              type="submit"
            >
              {updateTransaction.isPending ? t('common.saving') : t('plan.limitSave')}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
